import axiosInstance from "../helpers/axiosInstance";

const DEFAULT_PARTIAL_FULFILLMENT_INTERVAL_MS = 1000
const DEFAULT_PARTIAL_FULFILLMENT_MAX_ATTEMPTS = 120

export const fetchOrders = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms`, { params })
    return res.data
}

export const fetchPendingOrders = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/pending`, { params })
    return res.data
}

export const fetchProcessingOrders = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/processing`, { params })
    return res.data
}

export const fetchShippedOrders = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/shipped`, { params })
    return res.data
}

export const fetchShippedOrdersSummary = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/summary/shipped`, { params })
    return res.data
}

export const fetchPickAndShipOrders = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/pick_n_ship/wms`, { params })
    return res.data
}

export const fetchOrder = async (orderId) => {
    if(orderId===undefined){
        return null
    }
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/${orderId}`)
    return res.data
}

// Staff view of why a backordered order is short of stock, with the order's-warehouse locations
// holding the blocked units. Store users get 401 from the policy.
export const fetchOrderMissingStockDiagnosis = async (orderId) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/${orderId}/missing-stock-diagnosis`)
    return res.data
}

export const fetchOrderStockAllocation = async (orderId) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/${orderId}/stock-allocation`)
    return res.data
}

export const getPickingList = async (pickingOrderId) => {
    if(pickingOrderId===undefined){
        return null
    }
  
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/picking-orders/${pickingOrderId}/picking_list`, {responseType: 'blob'});
    
    const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
  
    // Open the PDF in a new tab
    window.open(pdfUrl);
}  

/**
 * @param order {{shippingMethod: {id: string, name: string}, shippingDate: string, numberShipping: string, orderNumber: string, referenceName: string}}
 * @param shippingAddress {{status: string, firstName: string, lastName: string, phone: string, address: string, country: {id: string, name: string}, city: string, province: string, zipCode: string}}
 * @param billingAddress {{status: string, firstName: string, lastName: string, phone: string, address: string, country: {id: string, name: string}, city: string, province: string, zipCode: string}}
 */
const getOrderData = (order, shippingAddress, billingAddress) => {
    const data = {
        shipping_method: order.shippingMethod.id,
        shipping_date: order.shippingDate,
        shipping: {
            first_name: shippingAddress.firstName,
            last_name: shippingAddress.lastName,
            phone: shippingAddress.phone,
            address1: shippingAddress.address,
            country: shippingAddress.country.name,
            city: shippingAddress.city,
            province: shippingAddress.province,
            zipCode: shippingAddress.zipCode
        },

        billing: {
            first_name: billingAddress.firstName,
            last_name: billingAddress.lastName,
            phone: billingAddress.phone,
            address1: billingAddress.address,
            country: billingAddress.country.name,
            city: billingAddress.city,
            province: billingAddress.province,
            zipCode: billingAddress.zipCode
        },
    }
    return data
}

/**
 *
 * @param order {{shippingMethod: {id: string, name: string}, shippingDate: string, numberShipping: string, orderNumber: string, referenceName: string}}
 * @param products {[{id: integer, name: string, quantity: int, type: string}]}
 * @param shippingAddress {{status: string, firstName: string, lastName: string, phone: string, address: string, country: {id: string, name: string}, city: string, province: string, zipCode: string}}
 * @param billingAddress {{status: string, firstName: string, lastName: string, phone: string, address: string, country: {id: string, name: string}, city: string, province: string, zipCode: string}}
 * @param storeId {integer}
 * @returns {Promise<void>}
 */
export const updateOrder = async (
    id,
    order,
    shippingAddress,
    billingAddress,
    products= []
) => {
    const data = {
        ...getOrderData(order, shippingAddress, billingAddress),
        order_lines: products
    }
    const res = await axiosInstance.put(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/${id}`, data)
    return res.data
}

export const pickAllOrderStocks = async (orderId) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/${orderId}/pick-all-stock`)
    return res.data
}

export const awaitInspection = async (returnId, cameraStatus = null) => {
    const body = cameraStatus ? { camera_status: cameraStatus } : {}
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/returns/${returnId}/await-inspection`, body)
    return res.data
}

export const putPickingOrderInReview = async (order_id, products, eraseShippingData) => {
    const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/picking-orders/${order_id}/put-in-review`, {
        products: products,
        erase_shipping_data: eraseShippingData
    })
    return res.data
}

export const bulkPickOrderStock = async function(orderId) {
    const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/picked`, { order_id: orderId })
    return res.data
}

export const createShippingIncidents = async (order_id, category) => {
    const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-incidents/create`, 
        { order_id: order_id, category: category  })
    return res.data
}

export const reviewShippingIncidents = async (incident_id) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-incidents/review`, 
        { incident_id: incident_id })
    return res.data
}

export const resolveShippingIncidents = async (incident_id, resolution) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-incidents/resolve`, 
        { incident_id: incident_id, resolution: resolution })
    return res.data
}

export const resetShippingIncidents = async (incident_id) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-incidents/reset`, 
        { incident_id: incident_id })
    return res.data
}

export const updateShippingStatus = async (order_id, status, description) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/update_shipping_status`, 
        { order_id: order_id, shipping_status: status, description: description })
    return res.data
}

export const getShippingGroupCostsForPackage = async (order_id, shipping_package_ids) => {
    let params = {
        shipping_package_ids: shipping_package_ids
        // forced_weight_estimate
    }
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/${order_id}/shipping-group-costs`, { params })
    return res.data
}

export const findOrderReturn = async (params) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/find-order-return`, { params })
    return res.data
}

/**
 * Bulk-reassign shipping labels to a carrier.
 * @param {Object} params
 * @param {string[]} params.scanned_labels - List of shipping numbers to reassign.
 * @param {string}   params.carrier_name   - Target carrier (e.g. "UBER").
 * @param {string}   params.delivery_type  - Delivery type (e.g. "SameDay").
 * @param {number}   params.warehouse_id   - Required. The warehouse the labels belong to.
 *                                          Returns { batch_id } on success.
 *                                          Returns 400 with { error, not_found_labels,
 *                                          cross_warehouse_labels } on mismatch.
 */
export const OrderReassingCarrierBulk = async (params) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/reassign-carrier-bulk`, params)
    return res.data
}

export const fetchOrderReassingCarrierBulkTracker = async function (batch_id) {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/user-async-tasks/track_batch/${batch_id}`)
    return res.data
}

export const setRemMaxPackingTime = async (orderId, maxPackingTime) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/${orderId}/max_packing_time`, { max_packing_time: maxPackingTime })
    return res.data
}

export const getOrderPackageRestrictions = async (orderId) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/fulfillment-tasks/package-restrictions/${orderId}`)
    return res.data
}

export const getOrderRecommendedShippingPackages = async(orderId) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/fulfillment-tasks/recommended-shipping-packages/${orderId}`)
    return res.data
}

export const partialFulfillOrdersInReview = async (pickingOrderIds) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/fulfillment-tasks/edit-in-review-order-for-partial-fulfillment`, { picking_order_ids: pickingOrderIds })
    return res.data
}

// CUBBO-2461 F3: partial fulfillment ("tarja") for a stuck B2B (picking-tasks)
// order. Returns { batch_id }; poll the same track_batch tracker as the classic
// in-review path (fetchPartialFulfillmentResult) — identical result contract, so
// the confirmation dialog + completion UI are reused.
export const partialFulfillB2b = async (pickingOrderId) => {
    const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/picking-orders/${pickingOrderId}/partial-fulfill-b2b`, {})
    return res.data
}

export const fetchPartialFulfillmentBatchTracker = async function (batch_id) {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/user-async-tasks/track_batch/${batch_id}`)
    return res.data
}

export const fetchPartialFulfillmentResult = async (batch_id, { intervalMs = DEFAULT_PARTIAL_FULFILLMENT_INTERVAL_MS, maxAttempts = DEFAULT_PARTIAL_FULFILLMENT_MAX_ATTEMPTS } = {}) => {
  let attempts = 0
  while (attempts < maxAttempts) {
    const data = await fetchPartialFulfillmentBatchTracker(batch_id)
    const isComplete = !!data?.is_complete
    if (isComplete) {
      const failures = data?.failures || 0
      const failInfo = data?.fail_info || []
      if (failures > 0) {
        const first = failInfo[0] || {}
        const message = first?.message || first?.error_class || 'Partial fulfillment failed'
        const err = new Error(message)
        err.data = { failures, failInfo }
        throw err
      }
      return {
        items: data?.removed_stock_units_for_partial_fulfillment || [],
        cancelOrder: !!data?.cancel_order,
        failures,
        failInfo,
      }
    }
    attempts += 1
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  throw new Error('Timeout while tracking partial fulfillment')
}

// Counts the errors that can already be retried in this warehouse. Feeds the sidebar menu
// badge, following the same pattern as countPendingClaims.
export const countPendingRetryFulfillmentErrors = async (warehouseId = null) => {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/fulfillment-errors/count-pending-retry`, { params: {warehouse_id: warehouseId} })
    return res.data
}

// Self-service quantity adjustment for REM orders
export const previewAdjustLineQuantities = async (orderId, adjustments) => {
    const res = await axiosInstance.post(
        `${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/${orderId}/adjust_line_quantities/preview`,
        { adjustments }
    )
    return res.data
}

export const applyAdjustLineQuantities = async (orderId, payload) => {
    // payload: { adjustments: [{ sku_or_upc, new_quantity }], reason, requested_by_account }
    const res = await axiosInstance.patch(
        `${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/${orderId}/adjust_line_quantities`,
        payload
    )
    return res.data
}
